/**
 * Downscale and re-encode an image in the browser before upload.
 *
 * The media-manager has always resized what it uploads (see `VARIANT_CONFIGS`
 * in `media-manager/process-handler.ts`), but the community-post composer
 * uploaded the file exactly as picked — so a modern phone photo went to R2 at
 * its full 4000px / 8MB, and a member could push a 100MB image through the
 * per-file ceiling. The feed never renders an attachment wider than ~1000px, so
 * everything past {@link MAX_EDGE_PX} is storage and download cost for pixels
 * nobody sees.
 *
 * Mirrors the media-manager's settings deliberately: same resizer, same webp
 * output, so both upload paths produce comparable objects.
 *
 * Best-effort by design — an unusual file, a decoder the browser dislikes, or a
 * re-encode that comes out *bigger* all fall back to the original. Compression
 * is never allowed to block or fail an upload.
 */
import { readAndCompressImage } from 'browser-image-resizer';

/**
 * Longest edge kept. The feed renders attachments at roughly a third of this,
 * so it leaves plenty of headroom for retina displays and the lightbox while
 * still cutting a typical phone photo by an order of magnitude.
 */
const MAX_EDGE_PX = 1920;

/** Matches the media-manager's variant quality. */
const QUALITY = 0.92;

/**
 * Formats that must pass through untouched.
 *
 * The resizer draws to a canvas and reads a single frame back, so a GIF would
 * lose its animation and an SVG would be flattened to a fixed-size raster.
 */
const PRESERVED_TYPES = new Set(['image/gif', 'image/svg+xml']);

/** Swap the extension for `.webp`, since the bytes are no longer the original format. */
const toWebpName = (name: string): string => `${name.replace(/\.[^.]+$/, '')}.webp`;

export const compressImageFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/') || PRESERVED_TYPES.has(file.type)) {
    return file;
  }

  try {
    const blob = await readAndCompressImage(file, {
      quality: QUALITY,
      maxWidth: MAX_EDGE_PX,
      maxHeight: MAX_EDGE_PX,
      mimeType: 'image/webp',
    });

    // An already-small, already-optimised image can come out larger as webp.
    // Uploading the bigger of the two would be a pure loss.
    if (blob.size >= file.size) {
      return file;
    }

    return new File([blob], toWebpName(file.name), { type: 'image/webp', lastModified: file.lastModified });
  } catch {
    return file;
  }
};
